import { computed, ref } from "vue"
import emailValidator from "../../foundation/js/inputValidators/emailValidator"
import notEmptyInput from "../../foundation/js/inputValidators/notEmptyInput"
import phoneValidator from "../../foundation/js/inputValidators/phoneValidator"
import isBoolean from "../../foundation/js/inputValidators/isBoolean"
import getContactedSubmit from "../../foundation/js/signupFlows/getContactedSubmit"
import getGeneralComputedValues from "../../foundation/js/signupFlows/getGeneralComputedValues"
import handleValidateInputAndProceed from "../../foundation/js/signupFlows/handleValidateInputAndProceed"

import SignupStepReceipt from "../../../../Features/Blocks/Newsletters/NewslettersSignup/SignupStepReceipt/SignupStepReceipt.vue"
import ContactStepIntro from "../ContactStepIntro/ContactStepIntro.vue"
import ContactStepBasicContent from "../ContactStepOnboardingContent/ContactStepOnboardingContent.vue"
import ContactStepCategoryContent from "../ContactStepCategoryContent/ContactStepCategoryContent.vue"
import ContactStepComments from "../ContactStepComments/ContactStepComments.vue"
import cprValidator from "../../foundation/js/inputValidators/cprValidator"
import { useRecaptchaToken } from "../../composables/use-recaptcha.composable"

export default {
  name: "ContactCardBasic",
  components: {
    ContactStepIntro,
    ContactStepBasicContent,
    ContactStepCategoryContent,
    ContactStepComments,
    SignupStepReceipt,
  },
  props: {
    antiForgeryToken: {
      type: String,
      required: true,
    },
    type: {
      type: String,
      required: true,
      default: "basic", // can be "basic" or "general"
    },
    labels: {
      type: Object,
      required: true,
    },
    formId: {
      type: String,
      required: true,
    },
    formGuid: {
      type: String,
      required: true,
    },
    formIdentifier: {
      type: String,
      required: true,
    },
    formTypeName: {
      type: String,
      required: true,
    },
    apiUrl: {
      type: String,
      required: true,
    },
    newsletterApiUrl: {
      type: String,
      required: true,
    },
    categories: {
      type: Array,
      required: false,
      default: () => [],
    },
    newsletters: {
      type: Array,
      required: false,
      default: () => [],
    },
    alignIntroImageRight: {
      type: Boolean,
      required: false,
      default: false,
    },
    skipFirstStep: {
      type: Boolean,
      required: false,
      default: false
    },
    imageSource: {
      type: String,
      required: true,
    },
    imageAltText: {
      type: String,
      required: false,
    },
    backgroundColor: {
      type: String,
      required: false,
      default: null,
    },
    assignTo: {
      type: String,
      required: true,
    },
    enableUserTrack: {
      type: Boolean,
      default: false,
      required: false
      },
      redirectUrl: {
          type: String,
          required: false
      },
      alwaysShowCprField: {
          type: Boolean,
          default: false,
          required: false
      }
  },
  setup(props) {
    const { getRecaptchaToken } = useRecaptchaToken();

    const bgColorClassName = computed(() => {
      return props.backgroundColor ? `nyk-contact-card--${props.backgroundColor}` : ""
    })

    const imagePosClassName = computed(() => {
      return props.alignIntroImageRight ? "nyk-contact-card--image-right" : ""
    })

    const contactCardClassNames = computed(() => {
      return `${bgColorClassName.value} ${imagePosClassName.value}`
    })

    const notRequiredInput = (input) => {
      return true
    }

    // this object contains the entire set of inputs available for the complete form.
    const userInput = ref([
      {
        field: "firstname",
        value: "",
        validator: notEmptyInput,
        isValid: false,
      },
      {
        field: "lastname",
        value: "",
        validator: notEmptyInput,
        isValid: false,
      },
      {
        field: "email",
        value: "", // you can prefill the inputfield by adding a value here.
        validator: emailValidator,
        isValid: false,
      },
      {
        field: "phone",
        value: "", // you can prefill the inputfield by adding a value here.
        validator: phoneValidator,
        isValid: false,
      },
      {
        field: "comment",
        value: "", // you can prefill the inputfield by adding a value here.
        validator: notRequiredInput,
        isValid: false,
      },
      {
        field: "cpr",
        value: "",
        validator: cprValidator, // should only be validated if the user is a customer
        isValid: false, // is valid as empty string if the user is not a customer
      }
    ])

    // this object contains stepinformation.
    // all steps are described here
    let stepsConfigAndOrder = ref([
      {
        step: 0,
        name: "ContactStepIntro",
        completed: props.skipFirstStep,
        isCountableStep: false,
        texts: {
          title: props.labels.title,
          summary: props.labels.introSummary,
          next: props.labels.nextButtonText
        },
      },
      {
        step: 1,
        name: "ContactStepBasicContent",
        completed: false,
        isCountableStep: true,
        texts: {
          title: props.labels.title,
          summary: props.labels.summary,
          next: props.labels.nextButtonText
        },
      },
      {
        step: 2,
        name: "ContactStepComments",
        completed: false,
        isCountableStep: true,
        texts: {
          title: props.labels.title,
            next: props.labels.submitLabel !== null &&
                props.labels.submitLabel !== undefined &&
                props.labels.submitLabel !== ""
                ? props.labels.submitLabel
                : props.labels.nextButtonText
        }
        ,
      },
      {
        step: 3,
        name: "SignupStepReceipt",
        completed: false,
        isCountableStep: false,
        redirectToNewPage: props.redirectUrl,
        texts: {
          title: props.labels.receiptTitle,
          summary: props.labels.receipt,
        },
      },
    ])

    const displayInputError = ref(false)

    const {
      getNamedFieldInputData,
      resetSignupForm,
      currentStepName,
      currentStepTexts,
      currentStepOptions,
      hasPreviousStep,
      stepCounterText,
      showFieldErrorMessage,
      returnToPreviousStep,
    } = getGeneralComputedValues(userInput.value, stepsConfigAndOrder.value)

    const validateInputAndProceed = function (inputData) {
      const validSuccess = handleValidateInputAndProceed(
        inputData,
        userInput.value,
        stepsConfigAndOrder.value,
        currentStepName.value,
        submitData
      )

      displayInputError.value = validSuccess
    }

    const submitData = async function (inputData, redirectToPage = null) {
      const body = {
        formIdentifier: `${props.formTypeName};${props.formIdentifier}`,
        formId: props.formId,
        formGuid: props.formGuid,
        firstName: inputData.find((field) => field.field === "firstname").value,
        lastName: inputData.find((field) => field.field === "lastname").value,
        email: inputData.find((field) => field.field === "email").value,
        phone: inputData.find((field) => field.field === "phone").value,
        comment: inputData.find((field) => field.field === "comment").value,
        cpr: inputData.find((field) => field.field === "cpr").value,
        assignTo: props.assignTo,
        enableUserTrack: props.enableUserTrack,
        isHomeowner: false,
        isUrgent: true
      }

      const formularInformation = {
        formName: props.formTypeName,
        formId: props.formIdentifier
      }
       
        await getContactedSubmit(body,
            props.apiUrl,
            formularInformation,
            props.antiForgeryToken,
            await getRecaptchaToken()
        )

        try {
            if (typeof redirectToPage === "string") {
                window.location.href = redirectToPage;
            }
        } catch {
            console.error("Invalid redirect URL:", redirectToPage);
        }  
    }

    return {
      currentStepTexts,
      currentStepOptions,
      displayInputError,
      currentStepName,
      getNamedFieldInputData,
      validateInputAndProceed,
      stepCounterText,
      returnToPreviousStep,
      showFieldErrorMessage,
      hasPreviousStep,
      resetSignupForm,
      contactCardClassNames
    }
  },
}
